<template>
    <v-row>
        <v-col cols="3" class="py-0">
            <date-picker :label="$t('Start date')"
                         :disabled="!canEdit"
                         :value="item.date_from" :error-messages="errors[`${index}.date_from`]"
                         @input="onSetStartDate"/>
        </v-col>
        <v-col cols="3" class="py-0">
            <date-picker clearable
                         :disabled="!canEdit"
                         :label="$t('End date')"
                         :value="item.date_until" :error-messages="errors[`${index}.date_until`]"
                         @input="onSetDateUntil"/>
        </v-col>
        <v-col cols="12" class="pb-0">
            <slot name="insert"/>
        </v-col>
        <v-col cols="12" class="pb-0">
            <div class="title">{{ $t('Working hours') }}</div>
            <v-radio-group class="mt-4 mb-4" row hide-details v-model="workingHoursType" @change="onChangeWorkingHoursType">
                <v-radio color="primary" value="standard" :disabled="!canEdit">
                    <template v-slot:label>
                        <div class="body-2">
                            {{ $t('Mon-Fri, 40h weekly, 8h daily') }}
                        </div>
                    </template>
                </v-radio>
                <v-radio color="primary" value="custom" :disabled="!canEdit">
                    <template v-slot:label>
                        <div class="body-2">
                            {{ $t('Customize') }}
                        </div>
                    </template>
                </v-radio>
            </v-radio-group>
        </v-col>
        <v-col v-if="workingHoursType === 'custom'" cols="10" class="py-0 d-flex">
            <div
                v-for="workingHourItem in workingHourItems"
                :key="workingHourItem.key"
                class="px-1"
            >
                <text-field
                    :label="workingHourItem.label"
                    :disabled="!canEdit"
                    :value="getWorkingHourValue(item, workingHourItem.key)"
                    :error-messages="errors[`${index}._data.${workingHourItem.key}`]"
                    @blur="onSetWorkingHourValue(workingHourItem.key, $event.target.value)"
                />
            </div>
        </v-col>
        <v-col v-if="workingHoursType === 'custom'" cols="2" class="py-0">
            <text-field
                :label="$t('Total')"
                disabled
                :value="getTotalWorkingHourDuration(item)"
            />
        </v-col>
        <v-col cols="12">
            <div class="title">{{ $t('Settings') }}</div>
            <div class="mt-2">
                <switch-button
                    :disabled="!canEdit"
                    :value="item.work_holidays"
                    :label="$t('Work on holidays')"
                    @change="onSetWorkHolidays"
                />
            </div>
        </v-col>
    </v-row>
</template>

<script>
import AppStoreDialogSidebar from "@/components/AppStore/AppStoreDialogSidebar";
import {$t} from "@/i18n";
import {computed, onMounted, ref} from "@vue/composition-api";
import {
    formatTimeDuration,
    parseTimeDuration,
    weekdayNames
} from "@/plugins/formatting";
import ExpansionPanels from "@/components/ExpansionPanels/Index";
import Vue from "vue";
import {deepClone} from "@/helpers/helpers";

export default {
    name: "MainFlextimeAgreementContent",
    components: {
        AppStoreDialogSidebar,
        ExpansionPanels
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        errors: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        canEdit: {
            type: Boolean,
            default: true
        }
    },
    setup(props, ctx) {
        const workingHoursType = ref('custom');

        const firstDayOfWeek = computed(function () {
            return Vue.formatting.first_week_day
        })

        const workingHourItems = computed(() => {
            let items = [
                {
                    key: 'monday',
                    label: weekdayNames()[1]
                },
                {
                    key: 'tuesday',
                    label: weekdayNames()[2]
                },
                {
                    key: 'wednesday',
                    label: weekdayNames()[3]
                },
                {
                    key: 'thursday',
                    label: weekdayNames()[4]
                },
                {
                    key: 'friday',
                    label: weekdayNames()[5]
                },
                {
                    key: 'saturday',
                    label: weekdayNames()[6]
                }
            ]

            let sunday = {
                key: 'sunday',
                label: weekdayNames()[0]
            }

            if (firstDayOfWeek.value % 7 === 0) {
                items.unshift(sunday)
            } else {
                items.push(sunday)
            }

            return items
        })

        const getWorkingHourValue = (item, key) => {
            if (!(item._data && item._data[key])) {
                // show empty, not 0:00 hours
                return '' // formatTimeDuration(0, '', true, true)
            }

            return formatTimeDuration(item._data[key], '', true, true)
        }

        const getTotalWorkingHourDuration = (item) => {
            if (!item._data) {
                return 0
            }

            let total = 0

            total += item._data.monday
            total += item._data.tuesday
            total += item._data.wednesday
            total += item._data.thursday
            total += item._data.friday
            total += item._data.saturday
            total += item._data.sunday

            return formatTimeDuration(total, '', true, true)
        }

        const onSetStartDate = (value) => {
            const errorsCopy = deepClone(props.errors)
            errorsCopy[`${props.index}.date_from`] = null

            ctx.emit('update-item-start-date', value)
            ctx.emit('update-errors', errorsCopy)
        }

        const onSetDateUntil = (value) => {
            const errorsCopy = deepClone(props.errors)
            errorsCopy[`${props.index}.date_until`] = null

            ctx.emit('update-item-date-until', value)
            ctx.emit('update-errors', errorsCopy)
        }

        const onSetWorkingHourValue = (key, value) => {
            const itemCopy = deepClone(props.item)

            let parsedValue = parseTimeDuration(value, true);
            ctx.emit('update-item-working-hour', {key, value: parsedValue})
        }

        const onSetWorkHolidays = (value) => {
            ctx.emit('update-item-work-holidays', value)
        }

        onMounted(() => {
            if (!props.item.id) {
                workingHoursType.value = 'standard'
            } else if (
                props.item._data.monday === (60 * 60 * 8) &&
                props.item._data.tuesday === (60 * 60 * 8) &&
                props.item._data.wednesday === (60 * 60 * 8) &&
                props.item._data.thursday === (60 * 60 * 8) &&
                props.item._data.friday === (60 * 60 * 8) &&
                props.item._data.saturday === 0 &&
                props.item._data.sunday === 0
            ) {
                workingHoursType.value = 'standard'
            }
        })

        const onChangeWorkingHoursType = (value) => {
            if (value === 'standard') {
                props.item._data.monday = (60 * 60 * 8)
                props.item._data.tuesday = (60 * 60 * 8)
                props.item._data.wednesday = (60 * 60 * 8)
                props.item._data.thursday = (60 * 60 * 8)
                props.item._data.friday = (60 * 60 * 8)
                props.item._data.saturday = 0
                props.item._data.sunday = 0
            }
        }

        return {
            parseTimeDuration,
            formatTimeDuration,
            workingHoursType,
            workingHourItems,
            getWorkingHourValue,
            getTotalWorkingHourDuration,
            onSetStartDate,
            onSetDateUntil,
            onSetWorkingHourValue,
            onSetWorkHolidays,
            onChangeWorkingHoursType
        };
    }
};
</script>
